import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import InvoiceHeader from '../components/invoice/InvoiceHeader';
import InvoiceForm from '../components/invoice/InvoiceForm';
import InvoiceTable from '../components/invoice/InvoiceTable';
import InvoiceFooter from '../components/invoice/InvoiceFooter';
import AddItemModal from '../components/modals/AddItemModal';
import AddRecipientModal from '../components/modals/AddRecipientModal';
import ShareModal from '../components/modals/ShareModal';
import TemplateModal from '../components/modals/TemplateModal';
import SaveOptionsModal from '../components/modals/SaveOptionsModal';
import { showToast } from '../utils/helpers';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from './../utils/api';
import { API_CONFIG } from './../utils/apiConfig';
import GenerateAccountCard from '../components/dashboard/GenerateAccountCard';
import AccountUnderReview from '../components/dashboard/AccountUnderReviewCard';
import GetNewAccountCard from '../components/dashboard/GetNewAccountCard';

const InvoicePage = () => {
  const { currentUser, currentAccountDetails, refreshUserData } = useAuth();

  // Handle account generation
  const handleGenerateAccount = async (formData) => {
    try {

      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        if (response.data.requires_verification) {
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000
          );
          return { requiresVerification: true };
        } else {
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      let errorMessage = 'Failed to generate account';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };

  // Creating New accounts 
  const handleNewWalletAccount = async (formData) => {
    try {
   
      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-existing-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        if (response.data.requires_verification) {
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000
          );
          return { requiresVerification: true };
        } else {
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      let errorMessage = 'Failed to generate account';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };

  // Enhanced invoice state management
  const [invoiceData, setInvoiceData] = useState(() => {
    try {
      const savedInvoice = localStorage.getItem('currentInvoice');
      return savedInvoice ? JSON.parse(savedInvoice) : getDefaultInvoiceState();
    } catch (error) {
      console.error('Error loading saved invoice:', error);
      return getDefaultInvoiceState();
    }
  });

  function getDefaultInvoiceState() {
    return {
      invoiceNumber: '',
      currentDate: new Date().toLocaleDateString(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      currency: 'NGN',
      items: [],
      recipient: null,
      companyDetails: null,
      accountDetails: null,
      notes: ''
    };
  }

  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveOptionsModal, setShowSaveOptionsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Enhanced localStorage persistence with error handling
  useEffect(() => {
    try {
      localStorage.setItem('currentInvoice', JSON.stringify(invoiceData));
    } catch (error) {
      console.error('Failed to save invoice to localStorage:', error);
      showToast('Failed to auto-save invoice data', 'warning');
    }
  }, [invoiceData]);

  useEffect(() => {
    initializeInvoice();
  }, []);

  // Reactively update company details when user data changes
  useEffect(() => {
    loadCompanyDetails();
  }, [currentUser, currentAccountDetails]);

  const initializeInvoice = async () => {
    try {
      setIsLoading(true);
      
      // Check for existing valid invoice
      const savedInvoice = localStorage.getItem('currentInvoice');
      
      if (savedInvoice) {
        const parsedInvoice = JSON.parse(savedInvoice);
        if (parsedInvoice.invoiceNumber) {
          try {
            const validationResponse = await api.post(API_CONFIG.INVOICES.VALIDATE_INVOICE_NUMBER, {
              invoiceNumber: parsedInvoice.invoiceNumber
            });
            
            if (validationResponse.data.success) {
              setInvoiceData(parsedInvoice);
              setIsLoading(false);
              return;
            }
          } catch (validationError) {
            console.log('Invoice validation failed, generating new one');
          }
        }
      }

      // Generate new invoice
      const response = await api.post(API_CONFIG.INVOICES.GENERATE_INVOICE_NUMBER);
      if (response.data.success) {
        setInvoiceData(prev => ({
          ...prev,
          invoiceNumber: response.data.invoiceNumber
        }));
      } else {
        throw new Error(response.data.error || 'Failed to generate invoice number');
      }
    } catch (error) {
      console.error('Error initializing invoice:', error);
      showToast('Failed to initialize invoice. Using fallback invoice number.', 'warning');
      
      // Robust fallback
      const fallbackInvoiceNumber = `INV-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      setInvoiceData(prev => ({
        ...prev,
        invoiceNumber: fallbackInvoiceNumber
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompanyDetails = () => {
    try {
      if (!currentUser) return;

      setInvoiceData(prev => ({
        ...prev,
        companyDetails: currentUser,
        accountDetails: currentAccountDetails
      }));
    } catch (error) {
      console.error('Error loading company details:', error);
      showToast('Failed to load company details', 'error');
    }
  };

  const clearAllData = async () => {
    try {
      if (!window.confirm('Are you sure you want to clear all invoice data? This action cannot be undone.')) {
        return;
      }

      const response = await api.post(API_CONFIG.INVOICES.GENERATE_INVOICE_NUMBER);
      
      if (response.data.success) {
        const newInvoiceNumber = response.data.invoiceNumber;
        
        localStorage.removeItem('currentInvoice');
        
        setInvoiceData({
          ...getDefaultInvoiceState(),
          invoiceNumber: newInvoiceNumber,
          companyDetails: invoiceData.companyDetails,
          accountDetails: invoiceData.accountDetails
        });

        showToast('Invoice data cleared successfully', 'success');
      } else {
        throw new Error('Failed to generate new invoice number');
      }
    } catch (error) {
      console.error('Error clearing invoice data:', error);
      showToast('Failed to clear invoice data', 'error');
    }
  };

  // Enhanced addItem function with better error handling and validation
  const addItem = useCallback((itemData) => {
    try {
      // Validate item data
      if (!itemData.description || !itemData.description.trim()) {
        showToast('Item description is required', 'error');
        return;
      }

      if (itemData.quantity <= 0) {
        showToast('Item quantity must be greater than 0', 'error');
        return;
      }

      if (itemData.price < 0) {
        showToast('Item price cannot be negative', 'error');
        return;
      }

      if (itemData.discount < 0) {
        showToast('Item discount cannot be negative', 'error');
        return;
      }

      if (itemData.discount > itemData.price) {
        showToast('Item discount cannot exceed price', 'error');
        return;
      }

      const tempItem = {
        ...itemData,
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        // Ensure all required fields are present
        description: itemData.description.trim(),
        quantity: Math.max(1, itemData.quantity),
        price: Math.max(0, itemData.price),
        discount: Math.max(0, Math.min(itemData.discount, itemData.price)),
        tax_rate: itemData.tax_rate || 0,
        amount: itemData.amount || (itemData.quantity * (itemData.price - itemData.discount))
      };

      setInvoiceData(prev => ({
        ...prev,
        items: [...prev.items, tempItem]
      }));
      
      showToast('Item added successfully', 'success');
      setShowAddItemModal(false);
    } catch (error) {
      console.error('Error adding item:', error);
      showToast('Failed to add item', 'error');
    }
  }, []);

  const deleteItem = useCallback((itemId) => {
    try {
      setInvoiceData(prev => ({
        ...prev,
        items: prev.items.filter(item => item.id !== itemId)
      }));
      
      showToast('Item deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting item:', error);
      showToast('Failed to delete item', 'error');
    }
  }, []);

  const saveRecipient = useCallback((recipientData) => {
    try {
      // Validate recipient data
      if (!recipientData.name || !recipientData.name.trim()) {
        showToast('Recipient name is required', 'error');
        return;
      }

      if (!recipientData.email || !recipientData.email.trim()) {
        showToast('Recipient email is required', 'error');
        return;
      }

      setInvoiceData(prev => ({
        ...prev,
        recipient: recipientData
      }));
      
      showToast('Recipient saved successfully', 'success');
      setShowAddRecipientModal(false);
    } catch (error) {
      console.error('Error saving recipient:', error);
      showToast('Failed to save recipient', 'error');
    }
  }, []);

  const validateInvoice = useCallback(() => {
    if (!invoiceData.recipient) {
      showToast('Please add recipient details before saving', 'warning');
      return false;
    }

    if (invoiceData.items.length === 0) {
      showToast('Please add at least one item before saving', 'warning');
      return false;
    }

    // Validate recipient has required fields
    if (!invoiceData.recipient.name || !invoiceData.recipient.email) {
      showToast('Recipient name and email are required', 'warning');
      return false;
    }

    // Validate all items
    for (const item of invoiceData.items) {
      if (!item.description || !item.description.trim()) {
        showToast('All items must have a description', 'warning');
        return false;
      }
      if (item.quantity <= 0) {
        showToast('All items must have a quantity greater than 0', 'warning');
        return false;
      }
      if (item.price < 0) {
        showToast('All items must have a non-negative price', 'warning');
        return false;
      }
    }

    return true;
  }, [invoiceData.recipient, invoiceData.items]);

  const handleSaveClick = useCallback(() => {
    if (!validateInvoice()) {
      return;
    }
    setShowSaveOptionsModal(true);
  }, [validateInvoice]);

  // Enhanced save invoice function with better error handling
  const saveInvoice = async (saveType = 'draft') => {
    if (!validateInvoice()) {
      return;
    }

    setIsSaving(true);
    setShowSaveOptionsModal(false);
    
    try {
      const payload = {
        InvoiceNumber: invoiceData.invoiceNumber,
        items: invoiceData.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount,
          tax_rate: item.tax_rate || 0
        })),
        recipient: invoiceData.recipient,
        dueDate: invoiceData.dueDate,
        notes: invoiceData.notes || '',
        status: saveType === 'draft' ? 'draft' : 'pending'
      };

      const response = await api.post(API_CONFIG.INVOICES.SAVE_INVOICE, payload);

      if (response.data.success) {
        const message = response.data.message || `Invoice ${saveType === 'draft' ? 'saved as draft' : 'saved'} successfully`;
        
        showToast(message, 'success');
        
        if (saveType === 'final') {
          // Generate new invoice for next creation
          const newResponse = await api.post(API_CONFIG.INVOICES.GENERATE_INVOICE_NUMBER);
          
          if (newResponse.data.success) {
            const newInvoiceNumber = newResponse.data.invoiceNumber;
            
            setInvoiceData(prev => ({
              ...prev,
              invoiceNumber: newInvoiceNumber,
              currentDate: new Date().toLocaleDateString(),
              dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
              items: [],
              recipient: null,
              notes: ''
            }));

            showToast('Ready to create new invoice', 'info');
          }
        }
      } else {
        throw new Error(response.data.error || 'Failed to save invoice');
      }
    } catch (error) {
      console.error('Error saving invoice:', error);
      
      let errorMessage = 'Network error. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const clearAllItems = useCallback(() => {
    if (invoiceData.items.length === 0) {
      showToast('No items to clear', 'info');
      return;
    }

    if (!window.confirm('Are you sure you want to clear all items?')) {
      return;
    }

    setInvoiceData(prev => ({
      ...prev,
      items: []
    }));
    showToast('All items cleared', 'info');
  }, [invoiceData.items.length]);

  const updateInvoiceNotes = useCallback((notes) => {
    setInvoiceData(prev => ({
      ...prev,
      notes
    }));
  }, []);

  // Memoized utility functions for better performance
  const formatCurrency = useCallback((amount, currency = 'NGN') => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }, []);

  const calculateItemTotal = useCallback((item) => {
    const discountedPrice = Math.max(0, (item.price || 0) - (item.discount || 0));
    return (item.quantity || 0) * discountedPrice;
  }, []);

  // Memoized calculations for better performance
  const calculations = useMemo(() => {
    const subtotal = invoiceData.items.reduce((total, item) => {
      return total + calculateItemTotal(item);
    }, 0);

    const totalDiscount = invoiceData.items.reduce((total, item) => {
      return total + ((item.discount || 0) * (item.quantity || 0));
    }, 0);

    const taxRate = invoiceData.companyDetails?.taxRate || 0;
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;

    return {
      subtotal,
      totalDiscount,
      tax,
      total
    };
  }, [invoiceData.items, invoiceData.companyDetails?.taxRate, calculateItemTotal]);

  const calculateSubtotal = useCallback(() => calculations.subtotal, [calculations.subtotal]);
  const calculateTotalDiscount = useCallback(() => calculations.totalDiscount, [calculations.totalDiscount]);
  const calculateTax = useCallback(() => calculations.tax, [calculations.tax]);
  const calculateTotal = useCallback(() => calculations.total, [calculations.total]);

  // Enhanced PDF generation with better error handling
  const generatePDF = async (templateId) => {
    try {
      const printContainer = document.createElement('div');
      printContainer.className = 'print-container';
      printContainer.style.width = '210mm';
      printContainer.style.padding = '20mm';
      printContainer.style.boxSizing = 'border-box';
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '0';
      printContainer.style.background = 'white';
      printContainer.style.fontFamily = "'Arial', 'Helvetica', sans-serif";
      printContainer.style.fontSize = '12px';
      
      const companyName = invoiceData.companyDetails?.company_name || invoiceData.companyDetails?.fullname || 'Your Company';
      const companyAddr = invoiceData.companyDetails?.company_address || '';
      const companyEmail = invoiceData.companyDetails?.company_email || '';
      const companyPhone = invoiceData.companyDetails?.company_phone || '';
      const companyLogo = invoiceData.companyDetails?.company_logo || '';

      // Create the clean invoice HTML with discount support
      printContainer.innerHTML = `
        <div class="invoice-print">
          <div class="invoice-header">
            <div class="company-info">
              ${companyLogo ? `<img src="${companyLogo}" alt="Logo" style="max-height: 60px; max-width: 120px; object-fit: contain; margin-bottom: 8px;" />` : ''}
              <h2 style="font-size: 16px;">${companyName}</h2>
              <p style="font-size: 11px;">${companyAddr}</p>
              <p style="font-size: 11px;">${companyEmail}</p>
              <p style="font-size: 11px;">${companyPhone}</p>
            </div>
            <div class="invoice-meta">
              <h1 style="font-size: 18px;">INVOICE</h1>
              <p style="font-size: 11px;"><strong>Invoice #:</strong> ${invoiceData.invoiceNumber}</p>
              <p style="font-size: 11px;"><strong>Date:</strong> ${invoiceData.currentDate}</p>
              <p style="font-size: 11px;"><strong>Due Date:</strong> ${invoiceData.dueDate}</p>
            </div>
          </div>
          
          <div class="invoice-parties">
            <div class="bill-to">
              <h3 style="font-size: 12px;">Bill To:</h3>
              ${invoiceData.recipient ? `
                <p style="font-size: 11px;"><strong>${invoiceData.recipient.name}</strong></p>
                <p style="font-size: 11px;">${invoiceData.recipient.email}</p>
                <p style="font-size: 11px;">${invoiceData.recipient.phone || ''}</p>
                <p style="font-size: 11px;">${invoiceData.recipient.address || ''}</p>
              ` : '<p style="font-size: 11px;">No recipient information</p>'}
            </div>
          </div>
          
          <table class="invoice-items">
            <thead>
              <tr>
                <th style="font-size: 10px;">Description</th>
                <th style="font-size: 10px;">Quantity</th>
                <th style="font-size: 10px;">Price</th>
                <th style="font-size: 10px;">Discount</th>
                <th style="font-size: 10px;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${invoiceData.items.map(item => {
                const hasDiscount = item.discount > 0;
                const itemTotal = calculateItemTotal(item);
                
                return `
                  <tr>
                    <td style="font-size: 10px;">${item.description}</td>
                    <td style="font-size: 10px;">${item.quantity}</td>
                    <td style="font-size: 10px;">
                      ${hasDiscount ? `
                        <div class="discounted-price">
                          <span class="original-price" style="text-decoration: line-through; color: #999; font-size: 9px;">
                            ${formatCurrency(item.price, invoiceData.currency)}
                          </span>
                          <br>
                          <span class="final-price" style="color: #2d7738; font-weight: bold; font-size: 10px;">
                            ${formatCurrency(item.price - item.discount, invoiceData.currency)}
                          </span>
                        </div>
                      ` : formatCurrency(item.price, invoiceData.currency)}
                    </td>
                    <td style="font-size: 10px;">${hasDiscount ? formatCurrency(item.discount, invoiceData.currency) : '-'}</td>
                    <td style="font-size: 10px;">${formatCurrency(itemTotal, invoiceData.currency)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="invoice-totals">
            <div class="total-row">
              <span style="font-size: 11px;">Subtotal:</span>
              <span style="font-size: 11px;">${formatCurrency(calculateSubtotal(), invoiceData.currency)}</span>
            </div>
            ${calculateTotalDiscount() > 0 ? `
              <div class="total-row discount-row">
                <span style="font-size: 11px;">Total Discount:</span>
                <span style="color: #dc3545; font-size: 11px;">-${formatCurrency(calculateTotalDiscount(), invoiceData.currency)}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span style="font-size: 11px;">Tax (${invoiceData.companyDetails?.taxRate || 0}%):</span>
              <span style="font-size: 11px;">${formatCurrency(calculateTax(), invoiceData.currency)}</span>
            </div>
            <div class="total-row grand-total">
              <span style="font-size: 12px;">Total:</span>
              <span style="font-size: 12px;">${formatCurrency(calculateTotal(), invoiceData.currency)}</span>
            </div>
          </div>
          
          <div class="invoice-footer">
            ${invoiceData.notes ? `
              <div class="notes">
                <p style="font-size: 11px;"><strong>Notes:</strong></p>
                <p style="font-size: 10px;">${invoiceData.notes}</p>
              </div>
            ` : ''}
            <p style="font-size: 11px;">Thank you for your business!</p>
            ${invoiceData.accountDetails ? `
              <div class="bank-details">
                <p style="font-size: 11px;"><strong>Bank Transfer Details:</strong></p>
                <p style="font-size: 10px;">Bank: ${invoiceData.accountDetails.bank_name || 'N/A'}</p>
                <p style="font-size: 10px;">Account Name: ${invoiceData.accountDetails.account_name || 'N/A'}</p>
                <p style="font-size: 10px;">Account Number: ${invoiceData.accountDetails.account_number || 'N/A'}</p>
              </div>
            ` : ''}
            ${invoiceData.companyDetails?.terms ? `
              <div class="terms">
                <p style="font-size: 11px;"><strong>Terms & Conditions:</strong></p>
                <p style="font-size: 10px;">${invoiceData.companyDetails.terms}</p>
              </div>
            ` : ''}
          </div>
        </div>
      `;
      
      // Add styles
      const style = document.createElement('style');
      style.textContent = `
        .invoice-print {
          font-family: Arial, sans-serif;
          color: #333;
          font-size: 12px;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .invoice-meta h1 {
          color: #333;
          margin: 0 0 10px 0;
          font-size: 18px;
        }
        .invoice-parties {
          margin-bottom: 30px;
        }
        .invoice-items {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
          font-size: 10px;
        }
        .invoice-items th {
          background-color: #f8f9fa;
          padding: 6px;
          text-align: left;
          border-bottom: 2px solid #dee2e6;
          font-size: 10px;
        }
        .invoice-items td {
          padding: 6px;
          border-bottom: 1px solid #dee2e6;
          font-size: 10px;
        }
        .invoice-totals {
          width: 100%;
          max-width: 300px;
          margin-left: auto;
          font-size: 11px;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 4px 0;
        }
        .discount-row {
          color: #dc3545;
        }
        .grand-total {
          font-weight: bold;
          font-size: 12px;
          border-top: 2px solid #333;
          margin-top: 10px;
          padding-top: 10px;
        }
        .invoice-footer {
          margin-top: 50px;
          padding-top: 20px;
          border-top: 1px solid #dee2e6;
          font-size: 10px;
        }
        .bank-details {
          margin-top: 15px;
          padding: 10px;
          background-color: #f8f9fa;
          border-radius: 4px;
        }
        .notes {
          margin-bottom: 15px;
          padding: 10px;
          background-color: #f0f8ff;
          border-radius: 4px;
          border-left: 4px solid #007bff;
        }
      `;
      printContainer.appendChild(style);
      
      document.body.appendChild(printContainer);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(printContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      document.body.removeChild(printContainer);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${invoiceData.invoiceNumber}-${templateId}.pdf`);
      
      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF', 'error');
      return false;
    }
  };

  const handlePrintWithTemplate = async (templateId) => {
    setIsLoading(true);
    setShowTemplateModal(false);
    
    try {
      showToast(`Generating PDF with ${templateId} template...`, 'info');
      const success = await generatePDF(templateId);
      
      if (success) {
        showToast('PDF downloaded successfully!', 'success');
      } else {
        showToast('Failed to generate PDF', 'error');
      }
    } catch (error) {
      showToast('Failed to generate PDF', 'error');
      console.error('PDF generation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized UI state for better performance
  const hasItems = invoiceData.items.length > 0;
  const hasRecipient = !!invoiceData.recipient;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading invoice...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-3">
      <div className="max-w-7xl mx-auto">
        {/* Account status components */}
        {!currentAccountDetails?.account_number ? (
          currentUser?.name_change_status === "under-review" ? (
            <div className="mb-6">
              <AccountUnderReview user={currentUser} />
            </div>
          ) : (
            <GenerateAccountCard 
              onGenerateAccount={handleGenerateAccount} 
              user={currentUser} 
            />
          )
        ) : currentAccountDetails?.is_pre_paymeo > 0 ? (
          <GetNewAccountCard 
            OnCreateNewAccount={handleNewWalletAccount} 
            user={currentUser} 
          />
        ) : (
          <>
            {/* Invoice Container */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-4">
                <InvoiceHeader
                  invoiceNumber={invoiceData.invoiceNumber}
                  companyDetails={invoiceData.companyDetails}
                  currentDate={invoiceData.currentDate}
                  dueDate={invoiceData.dueDate}
                  currency={invoiceData.currency}
                  onNotesChange={updateInvoiceNotes}
                  notes={invoiceData.notes}
                />

                <div className="my-6 border-t border-gray-200"></div>

                <InvoiceForm
                  recipient={invoiceData.recipient}
                  onAddRecipient={() => setShowAddRecipientModal(true)}
                  brandData={currentUser?.brandData}
                />

                {/* Enhanced Action Buttons */}
                <div className="flex flex-wrap gap-2 my-6">
                  <button
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm min-w-0 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowAddItemModal(true)}
                    disabled={isSaving}
                  >
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="truncate">Add Item</span>
                  </button>
                  
                  <button
                    className="flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm min-w-0 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowTemplateModal(true)}
                    disabled={isSaving || !hasItems}
                  >
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m4 4h6a2 2 0 002-2v-4a2 2 0 00-2-2h-6a2 2 0 00-2 2v4a2 2 0 002 2z" />
                    </svg>
                    <span className="truncate">Print</span>
                  </button>
                  
                  <button
                    className="flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm min-w-0 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={clearAllData}
                    disabled={isSaving}
                  >
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="truncate">Clear All</span>
                  </button>
                  
                  <button
                    className={`flex items-center px-3 py-2 rounded-lg transition-colors text-sm min-w-0 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                      isSaving 
                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    onClick={handleSaveClick}
                    disabled={isSaving || !hasRecipient || !hasItems}
                  >
                    {isSaving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                    ) : (
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className="truncate">{isSaving ? 'Saving...' : 'Save'}</span>
                  </button>
                  
                  <button
                    className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm min-w-0 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowShareModal(true)}
                    disabled={isSaving || !hasRecipient || !hasItems}
                  >
                    <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="truncate">Share</span>
                  </button>
                </div>

                {/* Items will automatically appear here when added */}
                <InvoiceTable
                  items={invoiceData.items}
                  currency={invoiceData.currency}
                  onDeleteItem={deleteItem}
                  formatCurrency={formatCurrency}
                  calculateItemTotal={calculateItemTotal}
                />

                <InvoiceFooter
                  items={invoiceData.items}
                  currency={invoiceData.currency}
                  companyDetails={invoiceData.accountDetails}
                  brandData={currentUser?.brandData}
                  formatCurrency={formatCurrency}
                  calculateSubtotal={calculateSubtotal}
                  calculateTotalDiscount={calculateTotalDiscount}
                  calculateTax={calculateTax}
                  calculateTotal={calculateTotal}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <AddItemModal
        show={showAddItemModal}
        onHide={() => setShowAddItemModal(false)}
        onAddItem={addItem}
        currency={invoiceData.currency}
      />

      <AddRecipientModal
        show={showAddRecipientModal}
        onHide={() => setShowAddRecipientModal(false)}
        onSaveRecipient={saveRecipient}
      />

      <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        invoiceData={invoiceData}
        currentUser={currentUser}
      />
      
      <TemplateModal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        onSelectTemplate={handlePrintWithTemplate}
      />

      <SaveOptionsModal
        show={showSaveOptionsModal}
        onHide={() => setShowSaveOptionsModal(false)}
        onSaveDraft={() => saveInvoice('draft')}
        onSaveAndNew={() => saveInvoice('final')}
        isSaving={isSaving}
      />
    </div>
  );
};

export default InvoicePage;